import React, { useState, useEffect } from 'react'

function AttachmentDialog({ isOpen, onClose, onSelect, downloadableType = null, downloadableId = null }) {
  const [activeTab, setActiveTab] = useState('upload') // 'upload' or 'search'
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [isSearching, setIsSearching] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [selectedFile, setSelectedFile] = useState(null)
  const [newFileName, setNewFileName] = useState('')

  // Search for existing downloads
  useEffect(() => {
    if (activeTab === 'search' && searchQuery.length > 2) {
      const delayTimer = setTimeout(() => {
        searchDownloads()
      }, 300)
      return () => clearTimeout(delayTimer)
    } else if (searchQuery.length <= 2) {
      setSearchResults([])
    }
  }, [searchQuery, activeTab])

  const searchDownloads = async () => {
    setIsSearching(true)
    try {
      const response = await fetch(`/downloads?q=${encodeURIComponent(searchQuery)}`, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        setSearchResults(data.downloads || [])
      }
    } catch (error) {
      console.error('Search error:', error)
    } finally {
      setIsSearching(false)
    }
  }

  const handleFileSelect = (e) => {
    const file = e.target.files[0]
    if (file) {
      setSelectedFile(file)
      // Generate default name from filename without extension
      const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '')
      setNewFileName(nameWithoutExt)
      setUploadError(null)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile) return

    setIsUploading(true)
    setUploadError(null)

    const formData = new FormData()
    formData.append('download[document]', selectedFile)
    formData.append('download[name]', newFileName || selectedFile.name)
    
    // Add optional downloadable association if provided
    if (downloadableType && downloadableId) {
      formData.append('download[downloadable_type]', downloadableType)
      formData.append('download[downloadable_id]', downloadableId)
    }

    try {
      const response = await fetch('/downloads', {
        method: 'POST',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.content
        },
        body: formData
      })

      const data = await response.json()

      if (data.success) {
        onSelect({
          url: data.download.url,
          name: data.download.name
        })
        onClose()
      } else {
        setUploadError(data.errors?.join(', ') || 'Upload failed')
      }
    } catch (error) {
      setUploadError('Upload failed: ' + error.message)
    } finally {
      setIsUploading(false)
    }
  }

  const handleSelectExisting = (download) => {
    onSelect({
      url: download.url,
      name: download.name
    })
    onClose()
  }

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      // Add a small delay to prevent state conflicts
      setTimeout(() => {
        setActiveTab('upload')
        setSelectedFile(null)
        setNewFileName('')
        setUploadError(null)
        setSearchQuery('')
        setSearchResults([])
      }, 100)
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      onMouseDown={(e) => {
        // Only close if clicking the backdrop
        if (e.target === e.currentTarget) {
          onClose()
        }
      }}
    >
      <div 
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-medium text-gray-900 dark:text-white">Add Attachment</h3>
        </div>

        <div className="flex-grow flex flex-col">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex -mb-px">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setActiveTab('upload')
                }}
                className={`py-2 px-6 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === 'upload'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Upload New
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  setActiveTab('search')
                }}
                className={`py-2 px-6 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                  activeTab === 'search'
                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Search Existing
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6 overflow-y-auto flex-grow">
            {activeTab === 'upload' && (
              <div className="space-y-4">
            
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Select File
                </label>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="block w-full text-sm text-gray-900 dark:text-gray-100 file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-gray-100 dark:file:bg-gray-700 file:text-gray-700 dark:file:text-gray-300 hover:file:bg-gray-200 dark:hover:file:bg-gray-600 file:cursor-pointer cursor-pointer"
                />
              </div>

              {selectedFile && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Display Name
                  </label>
                  <input
                    type="text"
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    placeholder="Enter display name"
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2"
                  />
                </div>
              )}

              {uploadError && (
                <div className="text-sm text-red-600 dark:text-red-400">{uploadError}</div>
              )}

              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault()
                  handleUpload()
                }}
                disabled={!selectedFile || isUploading}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-sm font-medium cursor-pointer"
              >
                {isUploading ? 'Uploading...' : 'Upload and Insert'}
              </button>
            </div>
              </div>
            )}

            {activeTab === 'search' && (
              <div className="space-y-4">
            
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search for files..."
              className="block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm px-3 py-2 mb-3"
            />

            {isSearching && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">Searching...</div>
            )}

            {searchResults.length > 0 && (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {searchResults.map((download) => (
                  <button
                    type="button"
                    key={download.id}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      handleSelectExisting(download)
                    }}
                    className="w-full text-left p-3 bg-gray-50 dark:bg-gray-700 rounded-md hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors cursor-pointer"
                  >
                    <div className="font-medium text-gray-900 dark:text-white">{download.name}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{download.filename}</div>
                  </button>
                ))}
              </div>
            )}

            {searchQuery.length > 2 && !isSearching && searchResults.length === 0 && (
              <div className="text-center py-4 text-gray-500 dark:text-gray-400">No files found</div>
            )}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault()
              onClose()
            }}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium cursor-pointer"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  )
}

export default AttachmentDialog